import type { PortalProforma, PortalRequiredDocument } from "@/features/portal/api";
import type { PortalClientSummary } from "@/features/portal/types";

export interface ChecklistItem {
  label: string;
  done: boolean;
}

export type JourneyState = "done" | "current" | "upcoming";

export interface JourneyStep {
  title: string;
  blurb: string;
  state: JourneyState;
}

export interface HubDerived {
  checklist: ChecklistItem[];
  /** Count of checklist items still waiting on the client. */
  outstanding: number;
  journey: JourneyStep[];
}

/**
 * Derive the home page's checklist + plain-language journey from the client's
 * real portal data. Pure (no fetch/render) so it's unit-testable.
 *
 * "Done" for a requested document is `status !== "PENDING"` (a WAIVED request no
 * longer needs the client). Financials has no server-side "finished" flag — the
 * firm flips `showFinancials` off once it's complete — so an *enabled* financials
 * flag means that step is still outstanding; when disabled it simply drops off
 * the list (we can't distinguish "finished" from "never enabled").
 */
export function deriveHub(input: {
  client: PortalClientSummary;
  requiredDocs: PortalRequiredDocument[];
  proformas: PortalProforma[];
}): HubDerived {
  const { client, requiredDocs, proformas } = input;

  const checklist: ChecklistItem[] = requiredDocs.map((d) => ({
    label: d.name,
    done: d.status !== "PENDING",
  }));
  if (client.showFinancials) {
    checklist.push({ label: "Tell us your monthly income & bills", done: false });
  }

  const outstanding = checklist.filter((i) => !i.done).length;
  const hasProposal = proformas.length > 0;

  const journey: JourneyStep[] = [
    {
      title: "We got to know your situation",
      blurb: "You told us what you're dealing with and we opened your case.",
      state: "done",
    },
    {
      title: "We're gathering your details",
      blurb:
        outstanding > 0
          ? "Documents and financial details — a couple of things are still with you."
          : "Everything we asked for is in. Thank you!",
      state: outstanding > 0 ? "current" : "done",
    },
    {
      title: "We prepare your proposal",
      blurb: hasProposal
        ? "Your tax team has shared a proposal with you."
        : "Once we have your details, we'll put together a plan.",
      state: hasProposal ? "done" : outstanding > 0 ? "upcoming" : "current",
    },
    {
      title: "You're all set",
      blurb: "Affordable next steps, with your tax team handling the back-and-forth.",
      state: hasProposal && outstanding === 0 ? "current" : "upcoming",
    },
  ];

  return { checklist, outstanding, journey };
}
